/**
 * Tracks "page-load related" requests (axios + fetch + manually-wrapped async work).
 * Lets callers wait until requests have settled after window.load using a quiet-window heuristic.
 *
 */

import type {
  AxiosTrackingInstance,
  PageLoadRequestsSettledResult,
  SettledResolveFn,
  TrackedRequestMeta,
} from "../types";
import {
  TRACKING_AXIOS_TOKEN_KEY,
  TRACKING_FETCH_WRAPPED_GLOBAL_FLAG,
  TRACKING_MAX_WAIT_AFTER_WINDOW_LOAD_MS,
  TRACKING_POST_LOAD_QUIET_PERIOD_MS,
} from "../constants/analytics.constants";

export type { PageLoadRequestsSettledResult, TrackedRequestMeta };

let isTrackingInitialized = false;
let hasWindowLoadFired = false;
let isPostLoadTrackingWindowClosed = false;
let inFlightCount = 0;

let settledResolve: SettledResolveFn | null = null;
let settledPromise: Promise<boolean> | null = null;

let isFetchWrapped = false;
let originalWindowFetch: typeof fetch | null = null;

const metaByToken = new Map<string, TrackedRequestMeta>();
const startPerfByToken = new Map<string, number>();

// Post-load settle heuristic:
// - after window.load, wait until there are no in-flight tracked requests AND no new starts
//   for POST_LOAD_QUIET_PERIOD_MS; also cap with MAX_WAIT_AFTER_WINDOW_LOAD_MS.
let lastTrackedStartWallClockMs = 0;
let apiBatchStartPerfMs: number | null = null;
let apiBatchEndPerfMs: number | null = null;
let trackedRequestCount = 0;
let quietPeriodTimerId: ReturnType<typeof setTimeout> | null = null;
let maxWaitAfterLoadTimerId: ReturnType<typeof setTimeout> | null = null;

let cachedApiBatchDurationMs = 0;

let tokenSequence = 0;

const nextTrackingToken = (): string => {
  tokenSequence = (tokenSequence + 1) % Number.MAX_SAFE_INTEGER;
  return `${Date.now().toString(36)}-${tokenSequence.toString(36)}`;
};

const getApiBatchStartPerfMs = (): number => {
  return apiBatchStartPerfMs ?? 0;
};

const getApiBatchEndPerfMs = (): number => {
  if (apiBatchStartPerfMs === null) return 0;
  if (isPostLoadTrackingWindowClosed) {
    return apiBatchEndPerfMs ?? apiBatchStartPerfMs + cachedApiBatchDurationMs;
  }
  return apiBatchEndPerfMs ?? performance.now();
};

const maybeResolveSettled = () => {
  if (!settledResolve) return;
  if (hasWindowLoadFired && isPostLoadTrackingWindowClosed && inFlightCount === 0) {
    const resolve = settledResolve;
    settledResolve = null;
    settledPromise = null;
    resolve(true);
  }
};

const clearTimer = (timer: ReturnType<typeof setTimeout> | null) => {
  if (timer === null) return;
  clearTimeout(timer);
};

const schedulePostLoadQuietPeriodCheck = () => {
  if (typeof window === "undefined") return;
  if (!hasWindowLoadFired || isPostLoadTrackingWindowClosed) return;

  clearTimer(quietPeriodTimerId);
  quietPeriodTimerId = setTimeout(() => {
    quietPeriodTimerId = null;
    if (isPostLoadTrackingWindowClosed) return;
    if (inFlightCount !== 0) return;
    if (Date.now() - lastTrackedStartWallClockMs < TRACKING_POST_LOAD_QUIET_PERIOD_MS) return;

    isPostLoadTrackingWindowClosed = true;
    const durationMs =
      apiBatchStartPerfMs !== null && apiBatchEndPerfMs !== null
        ? Math.max(0, apiBatchEndPerfMs - apiBatchStartPerfMs)
        : 0;
    cachedApiBatchDurationMs = durationMs;
    maybeResolveSettled();
  }, TRACKING_POST_LOAD_QUIET_PERIOD_MS);
};

const tryGetResourceTimingResponseEndPerfMs = (
  url: string,
  kind: TrackedRequestMeta["kind"],
  startedAtPerfMs: number
): number | null => {
  if (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function") {
    return null;
  }
  const entries = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
  if (!entries || entries.length === 0) return null;

  let expectedInitiator: string | null = null;
  if (kind === "axios") {
    expectedInitiator = "xmlhttprequest";
  } else if (kind === "fetch") {
    expectedInitiator = "fetch";
  }
  const startWindowMs = Math.max(0, startedAtPerfMs - 200);

  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const e = entries[i];
    if (!e || e.name !== url) continue;
    if (expectedInitiator && e.initiatorType !== expectedInitiator) continue;
    if (e.startTime < startWindowMs) continue;
    if (!Number.isFinite(e.responseEnd) || e.responseEnd <= 0) continue;
    return e.responseEnd;
  }

  return null;
};

const beginTrackedRequest = (meta?: TrackedRequestMeta): string | null => {
  if (!isTrackingInitialized || isPostLoadTrackingWindowClosed) return null;
  inFlightCount += 1;
  lastTrackedStartWallClockMs = Date.now();
  trackedRequestCount += 1;
  if (apiBatchStartPerfMs === null) {
    apiBatchStartPerfMs = performance.now();
  }
  const token = nextTrackingToken();
  startPerfByToken.set(token, performance.now());
  if (meta) {
    metaByToken.set(token, meta);
  }
  schedulePostLoadQuietPeriodCheck();
  return token;
};

const finishTrackedRequest = (token: string | null | undefined) => {
  if (!token) return;
  const meta = metaByToken.get(token);
  const startedAtPerfMs = startPerfByToken.get(token);
  const resourceEndPerfMs =
    meta?.url && startedAtPerfMs !== undefined
      ? tryGetResourceTimingResponseEndPerfMs(meta.url, meta.kind, startedAtPerfMs)
      : null;
  const endPerfMs = resourceEndPerfMs ?? performance.now();
  apiBatchEndPerfMs = endPerfMs;
  metaByToken.delete(token);
  startPerfByToken.delete(token);
  if (inFlightCount > 0) inFlightCount -= 1;
  maybeResolveSettled();
  if (hasWindowLoadFired && !isPostLoadTrackingWindowClosed && inFlightCount === 0) {
    schedulePostLoadQuietPeriodCheck();
  }
};

/**
 * Initialises page-load request tracking. Wraps native `fetch` to automatically
 * track in-flight requests. Call this as early as possible (e.g. in _app.tsx).
 *
 * NOTE: The 3in1cms version also wraps axios interceptors. If your app uses axios,
 * call `initAxiosTracking(axiosInstance)` separately after importing axios.
 */
export const initPageLoadRequestTracking = (): void => {
  if (typeof window === "undefined") return;
  if (isTrackingInitialized) return;
  isTrackingInitialized = true;

  hasWindowLoadFired = document.readyState === "complete";

  const startPostLoadTimers = () => {
    hasWindowLoadFired = true;
    lastTrackedStartWallClockMs = Date.now();
    clearTimer(maxWaitAfterLoadTimerId);
    maxWaitAfterLoadTimerId = setTimeout(() => {
      maxWaitAfterLoadTimerId = null;
      if (isPostLoadTrackingWindowClosed) return;
      isPostLoadTrackingWindowClosed = true;
      const durationMs =
        apiBatchStartPerfMs !== null ? Math.max(0, performance.now() - apiBatchStartPerfMs) : 0;
      cachedApiBatchDurationMs = durationMs;
      maybeResolveSettled();
    }, TRACKING_MAX_WAIT_AFTER_WINDOW_LOAD_MS);
    schedulePostLoadQuietPeriodCheck();
  };

  if (!hasWindowLoadFired) {
    window.addEventListener("load", startPostLoadTimers, { once: true });
  } else {
    startPostLoadTimers();
  }

  // Wrap native fetch
  if (!isFetchWrapped && typeof window.fetch === "function") {
    isFetchWrapped = true;
    const globalFlag = TRACKING_FETCH_WRAPPED_GLOBAL_FLAG;
    if ((window as unknown as Record<string, unknown>)[globalFlag]) {
      return;
    }
    (window as unknown as Record<string, unknown>)[globalFlag] = true;

    originalWindowFetch = window.fetch.bind(window);
    window.fetch = (async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      let url: string;
      if (typeof input === "string") {
        url = input;
      } else if (input instanceof URL) {
        url = input.toString();
      } else {
        url = input.url;
      }

      const isRequestObject = typeof input !== "string" && !(input instanceof URL);
      const rawMethod = init?.method ?? (isRequestObject ? input.method : "GET");
      const method = (rawMethod ?? "GET").toString().toUpperCase();
      const token = beginTrackedRequest({ kind: "fetch", name: `${method} ${url}`, url });
      try {
        const response = await (originalWindowFetch as typeof fetch)(input as RequestInfo, init);
        finishTrackedRequest(token);
        return response;
      } catch (e) {
        finishTrackedRequest(token);
        throw e;
      }
    }) as typeof fetch;
  }
};

/**
 * Optional: install axios interceptors for page-load tracking.
 * Call this with your shared axios instance if the app uses axios.
 *
 * @example
 *   import axios from "axios";
 *   import { initAxiosTracking } from "@repo/analytics/trackPageLoadRequests";
 *   initAxiosTracking(axios);
 */
export const initAxiosTracking = (axiosInstance: AxiosTrackingInstance): void => {
  axiosInstance.interceptors.request.use((config: any) => {
    const url = `${(config.baseURL ?? "") + (config.url ?? "")}`.trim();
    const token = beginTrackedRequest({
      kind: "axios",
      name: `${(config.method ?? "GET").toString().toUpperCase()} ${url}`.trim(),
      url,
    });
    config[TRACKING_AXIOS_TOKEN_KEY] = token;
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response: any) => {
      const token = response.config?.[TRACKING_AXIOS_TOKEN_KEY] as string | null;
      finishTrackedRequest(token);
      return response;
    },
    (error: unknown) => {
      const config = (error as { config?: Record<string, unknown> })?.config;
      const token = config?.[TRACKING_AXIOS_TOKEN_KEY] as string | null | undefined;
      finishTrackedRequest(token);
      if (error instanceof Error) return Promise.reject(error);
      const message =
        (error as { message?: unknown })?.message &&
        typeof (error as { message?: unknown }).message === "string"
          ? ((error as { message?: unknown }).message as string)
          : "Axios request failed";
      return Promise.reject(Object.assign(new Error(message), { cause: error }));
    }
  );
};

/**
 * Wraps arbitrary async work so it counts as a tracked "page-load" request.
 */
export const trackPageLoadAsyncWork = async <T>(
  work: () => Promise<T>,
  meta?: TrackedRequestMeta
): Promise<T> => {
  initPageLoadRequestTracking();
  const token = beginTrackedRequest(meta ?? { kind: "manual", name: "work" });
  try {
    return await work();
  } finally {
    finishTrackedRequest(token);
  }
};

const computeApiBatchDurationMs = (): number => {
  if (apiBatchStartPerfMs === null) return 0;
  if (isPostLoadTrackingWindowClosed) {
    return cachedApiBatchDurationMs;
  }
  if (apiBatchEndPerfMs !== null) {
    return Math.max(0, apiBatchEndPerfMs - apiBatchStartPerfMs);
  }
  return Math.max(0, performance.now() - apiBatchStartPerfMs);
};

/**
 * Returns a promise that resolves once all page-load requests have settled
 * (i.e. no in-flight tracked calls for 800 ms after window.load).
 */
export const waitForPageLoadRequestsToSettle = (
  timeoutMs = 60000
): Promise<PageLoadRequestsSettledResult> => {
  initPageLoadRequestTracking();

  if (hasWindowLoadFired && isPostLoadTrackingWindowClosed && inFlightCount === 0) {
    return Promise.resolve({
      settled: true,
      isTrackingWindowClosed: isPostLoadTrackingWindowClosed,
      inFlightCount,
      trackedRequestCount,
      apiBatchDurationMs: computeApiBatchDurationMs(),
      apiBatchStartPerfMs: getApiBatchStartPerfMs(),
      apiBatchEndPerfMs: getApiBatchEndPerfMs(),
    });
  }

  if (!settledPromise) {
    settledPromise = new Promise<boolean>((resolve) => {
      settledResolve = resolve;
      maybeResolveSettled();
    });
  }

  return Promise.race([
    settledPromise,
    new Promise<boolean>((resolve) => {
      setTimeout(() => resolve(false), timeoutMs);
    }),
  ]).then((settled) => ({
    settled,
    isTrackingWindowClosed: isPostLoadTrackingWindowClosed,
    inFlightCount,
    trackedRequestCount,
    apiBatchDurationMs: computeApiBatchDurationMs(),
    apiBatchStartPerfMs: getApiBatchStartPerfMs(),
    apiBatchEndPerfMs: getApiBatchEndPerfMs(),
  }));
};
